/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/leaveRequest.ts
 * PURPOSE: Centralized service layer for employee self-service leave requests —
 *          single source of truth for leave-request/leave-balance endpoint paths
 *          + HTTP methods, so a backend route rename can never silently drift out
 *          of sync with a hand-rolled `fetch()` call scattered in a page component.
 *          2026-09-24 (SC-024 update + SC-032 new, BS-024): added compensatory-leave
 *          balance (GET /compensatory-leave/my-balance) + public-holidays
 *          (GET /public-holidays?year=) read functions, and extended
 *          CreateLeaveRequestPayload for leave_type='compensatory' submits
 *          (BS-024-R10/R11/R15).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-24
 * COMPLIANCE: SDLC 6.5.0 + BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN §Route 6 (P0 fix)
 *             + BS-024-PHAN-DOAN-D-NHAN-VIEN-NGHI-PHEP (SC-024/SC-032)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (Documentation Permanence)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management, Employee Self-Service Leave
 * - Impact: Revenue Critical — 3 P0 bugs fixed here (S78-002/S78-003 route renames
 *   never propagated to the web frontend): `/leave-requests/my-requests` (404, stale)
 *   → `/leave-requests/my`; `/leave-requests/balance` (404, stale, silent no-op on
 *   error → infinite "Đang tải số dư...") → `/leave-balances/my`; cancel `PUT` (405)
 *   → `POST`.
 * - Stakeholders: Service Staff (NQH), Branch Managers, HR Team
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern (thin fetch wrapper, no framework dependency)
 * - Dependencies: fetch, localStorage (auth-storage / access_token — same pattern
 *   as `apiWrapper.ts`)
 *
 * @cross-references
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN.md
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-024-PHAN-DOAN-D-NHAN-VIEN-NGHI-PHEP.md
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-024-EMP-LEAVE, §SC-032-EMP-PUBLIC-HOLIDAYS
 * - API (SSOT for these routes): backend/app/api/v1/leave_request.py (`GET
 *   /leave-requests/my`, `GET /leave-balances/my`, `POST /leave-requests/{id}/cancel`,
 *   `POST /leave-requests`), backend/app/api/v1/compensatory_leave.py (`GET
 *   /compensatory-leave/my-balance`), backend/app/api/v1/public_holiday.py (`GET
 *   /public-holidays?year=`)
 * - Consumer: frontend/src/pages/LeaveRequestModern.tsx, frontend/src/pages/PublicHolidaysModern.tsx
 * - Pattern #15 fix (@cto 2026-08-21, PR #625 condition 1): getMyLeaveRequests
 *   THROWS on !ok — same contract as getMyLeaveBalance — so a failed fetch can
 *   never masquerade as an empty list ("empty giả"). getMyCompensatoryBalance /
 *   getPublicHolidays follow the same throw-on-!ok contract.
 * - ⚠️ KNOWN GAP (BS-024 annotation [5] "Từ: {earned_occasion}" for compensatory
 *   cards): backend's `_build_response()` (api/v1/leave_request.py) does NOT
 *   return `compensatory_balance_id` or `earned_occasion` on LeaveRequestDTO —
 *   there is no field to join a historical compensatory request against its
 *   consumed balance (which may already show remaining_days=0 and be absent
 *   from /compensatory-leave/my-balance). LeaveRequestModern.tsx therefore
 *   cannot render that third line and omits it — see that file's own note.
 */

export interface LeaveRequestDTO {
  id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  working_days: number;
  reason?: string;
  status: string;
  is_emergency: boolean;
  sla_deadline?: string;
  sla_breached: boolean;
  created_at?: string;
  auto_approved?: boolean;
}

export interface LeaveBalanceDTO {
  leave_type: string;
  total_days: number;
  used_days: number;
  remaining_days: number;
}

/** BS-024-R10/R11 (DD-0115) — one compensatory-leave grant still usable. */
export interface CompensatoryBalanceDTO {
  id: string;
  earned_occasion: string;
  earned_from: string; // ISO date
  remaining_days: number;
  expiry_date: string; // ISO date
}

/** BS-032-R03 (DD-0114) — one tenant-configured public holiday. */
export interface PublicHolidayDTO {
  id: string;
  name: string;
  start_date: string; // ISO date
  end_date: string | null; // ISO date, null = single-day holiday
}

export interface CreateLeaveRequestPayload {
  leave_type: string;
  // BS-024-R15/DD-0115 §2.5: required for the 4 normal types, MUST be
  // omitted for leave_type='compensatory' (server derives the date from the
  // chosen compensatory_balance_id — see _consume_compensatory_balance()).
  start_date?: string;
  end_date?: string;
  reason?: string;
  // BS-024-R10/R11: required when leave_type='compensatory', MUST be omitted
  // for every other type (server 422s a mismatch either way).
  compensatory_balance_id?: string;
}

/** Generic envelope so callers keep full control over success/error UI copy. */
export interface LeaveApiResult<T = any> {
  ok: boolean;
  status: number;
  data: T;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const tenantKey = parsed?.state?.tenantKey || parsed?.state?.tenant_key;
      if (tenantKey) headers['X-Tenant-Key'] = tenantKey;
    }
  } catch {
    /* ignore — unauthenticated request, backend will 401 */
  }
  return headers;
}

/**
 * GET /api/v1/leave-requests/my — employee's own leave requests.
 * FIX (P0/G1): was `/leave-requests/my-requests` (renamed by ticket S78-002,
 * never updated on web) → always 404'd → list always empty.
 * FIX (Pattern #15, @cto 2026-08-21, PR #625 condition 1): was
 * `if (!res.ok) return [];` — a silent fallback that showed a failed fetch as
 * "Chưa có đơn nghỉ phép nào" ("empty giả"). Now THROWS on !ok, same contract
 * as getMyLeaveBalance, so the caller can render a real error state.
 */
export async function getMyLeaveRequests(): Promise<LeaveRequestDTO[]> {
  const res = await fetch('/api/v1/leave-requests/my', { headers: getAuthHeaders() });
  if (!res.ok) {
    throw new Error(`Không thể tải danh sách đơn nghỉ phép (HTTP ${res.status}). Vui lòng thử lại.`);
  }
  const data = await res.json().catch(() => ({}));
  const items = data.leave_requests || data.items || data.data || [];
  return Array.isArray(items) ? items : [];
}

/**
 * GET /api/v1/leave-balances/my — employee's own leave balance.
 * FIX (P0/G2): was `/leave-requests/balance` (moved to its own router by ticket
 * S78-002, never updated on web) → always 404'd, and the old error branch set
 * NO state at all → balance screen stuck forever on "Đang tải số dư...".
 */
export async function getMyLeaveBalance(): Promise<LeaveBalanceDTO[]> {
  const res = await fetch('/api/v1/leave-balances/my', { headers: getAuthHeaders() });
  if (!res.ok) {
    throw new Error(`Không thể tải số dư nghỉ phép (HTTP ${res.status}).`);
  }
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.balances) ? data.balances : [];
}

/** POST /api/v1/leave-requests — create a new leave request. Contract already correct. */
export async function createLeaveRequest(
  payload: CreateLeaveRequestPayload
): Promise<LeaveApiResult> {
  const res = await fetch('/api/v1/leave-requests', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok || res.status === 201, status: res.status, data };
}

/**
 * POST /api/v1/leave-requests/{id}/cancel — cancel own leave request.
 * FIX (P0/G3): was `PUT` (backend only registers `POST`, ticket S78-003) → 405,
 * cancel silently never worked.
 */
export async function cancelLeaveRequest(requestId: string): Promise<LeaveApiResult> {
  const res = await fetch(`/api/v1/leave-requests/${requestId}/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

/**
 * GET /api/v1/compensatory-leave/my-balance — employee's own usable
 * compensatory-leave grants (BS-024-R10, card [2b] + dropdown [13]).
 * Empty array is a valid 200 response ("Chưa có ngày nghỉ bù nào"), not an
 * error — same throw-on-!ok contract as getMyLeaveBalance so a real fetch
 * failure never masquerades as "no compensatory leave".
 */
export async function getMyCompensatoryBalance(): Promise<CompensatoryBalanceDTO[]> {
  const res = await fetch('/api/v1/compensatory-leave/my-balance', { headers: getAuthHeaders() });
  if (!res.ok) {
    throw new Error(`Không thể tải số dư nghỉ bù (HTTP ${res.status}).`);
  }
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.balances) ? data.balances : [];
}

/**
 * GET /api/v1/public-holidays?year={YYYY} — tenant's official public
 * holidays for a year (BS-032-R03). A year with no data is a valid 200
 * `{holidays: []}` (BS-032-R05 EMPTY state), NOT a 404 — only a real HTTP
 * failure throws.
 */
export async function getPublicHolidays(year: number): Promise<PublicHolidayDTO[]> {
  const res = await fetch(`/api/v1/public-holidays?year=${year}`, { headers: getAuthHeaders() });
  if (!res.ok) {
    throw new Error(`Không thể tải danh sách ngày nghỉ lễ (HTTP ${res.status}).`);
  }
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.holidays) ? data.holidays : [];
}
